
import {Injectable} from '@angular/core';
import {
  HttpRequest, HttpHandler, HttpEvent,
  HttpInterceptor, HttpErrorResponse
} from '@angular/common/http';
import {catchError, Observable, switchMap, throwError} from 'rxjs';
import {AuthenticationService} from '../services/authentication.service';
import {JwtHelperService} from '@auth0/angular-jwt';
import {Router} from '@angular/router';
import {NotificationService} from '../notification/notification.service';
import {NotificationType} from '../enum/notification-type.enum';
import {MatDialog} from '@angular/material/dialog';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private unauthenticatedEndpoints: string[];

  constructor(
    private authenticationService: AuthenticationService,
    private jwtHelper: JwtHelperService,
    private router: Router,
    private notification: NotificationService,
    private dialog: MatDialog
  ) {
    this.unauthenticatedEndpoints = [
      `${this.authenticationService.host}/v1/staff/auth/login`,
      `${this.authenticationService.host}/v1/staff/auth/token/refresh`,
      `${this.authenticationService.host}/v1/public/configs/restaurant`,
    ];
  }

  intercept(httpRequest: HttpRequest<any>, httpHandler: HttpHandler): Observable<HttpEvent<any>> {
    if (this.unauthenticatedEndpoints.some(ep => httpRequest.url.includes(ep))) {
      return httpHandler.handle(httpRequest);
    }

    this.authenticationService.loadToken();
    const token = this.authenticationService.getToken();

    if (token && this.isTokenExpiredOrAboutToExpire(token, 10)) {
      return this.handle401Error(httpRequest, httpHandler);
    }

    return httpHandler.handle(this.addTokenHeader(httpRequest, token)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(httpRequest, httpHandler);
        }
        return throwError(() => error);
      })
    );
  }
  private isTokenExpiredOrAboutToExpire(token: string, bufferSeconds: number = 10): boolean {
    try {
      const decoded = this.jwtHelper.decodeToken(token);
      if (!decoded?.exp) return true;

      const expirationDate = new Date(decoded.exp * 1000);
      const now = new Date();
      const bufferMs = bufferSeconds * 1000;

      return expirationDate.getTime() - now.getTime() <= bufferMs;
    } catch {
      return true;
    }
  }
  private addTokenHeader(request: HttpRequest<any>, token: string | null): HttpRequest<any> {
    if (!token) return request;
    return request.clone({setHeaders: {Authorization: `Bearer ${token}`}});
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const refreshToken = this.authenticationService.getRefreshToken();
    if (!refreshToken) {
      this.redirectToLogin();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.authenticationService.handleTokenRefresh().pipe(
      switchMap((newToken: string | null) => {
        if (!newToken) {
          this.redirectToLogin();
          return throwError(() => new Error('Refresh failed'));
        }
        return next.handle(this.addTokenHeader(request, newToken));
      }),
      catchError(err => {
        this.redirectToLogin();
        return throwError(() => err);
      })
    );
  }

  private redirectToLogin(): void {
    this.authenticationService.clearSession();
    this.dialog.closeAll();
    this.router.navigate(['/auth/login']).then(() => {
      this.notification.notify(
        NotificationType.WARNING,
        'Veuillez vous connecter pour accéder à cette page',
        'Accès refusé'
      );
    });
  }
}
